import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TemplateReferencePayload } from '../lib/templateAiDrafting';

export interface Tab {
  id: string; // unique ID, usually the path
  path: string; // e.g. /cases/123
  title: string;
  type: 'case' | 'draft' | 'document' | 'new';
  entityId?: string;
  lastActiveAt: number;
  /** One-shot auto-draft trigger, scoped to this tab */
  pendingAutoDraft?: {
    instruction: string;
    /** Scaffold HTML snapshot at create time (avoids load race) */
    draftText?: string;
    templateMode?: boolean;
    exemplarMode?: boolean;
    templateTitle?: string;
    templateReference?: TemplateReferencePayload;
    createdAt: number;
  };
}

export interface PendingAutoDraftPayload {
  instruction: string;
  draftText?: string;
  templateMode?: boolean;
  exemplarMode?: boolean;
  templateTitle?: string;
  templateReference?: TemplateReferencePayload;
}

/** While set, Layout skips addTab for this path until the user navigates away. */
let suppressAutoTabForPath: string | null = null;

export function suppressAutoTab(path: string) {
  suppressAutoTabForPath = path;
}

export function shouldSuppressAutoTab(path: string) {
  return suppressAutoTabForPath === path;
}

export function clearSuppressAutoTab(path?: string) {
  if (path === undefined || suppressAutoTabForPath === path) {
    suppressAutoTabForPath = null;
  }
}

export function syncSuppressAutoTab(currentPath: string) {
  if (suppressAutoTabForPath && suppressAutoTabForPath !== currentPath) {
    suppressAutoTabForPath = null;
  }
}

/** Remove duplicate tab ids (keeps the later entry — fixes persisted strip corruption). */
export function dedupeWorkspaceTabs(tabs: Tab[]): Tab[] {
  const seen = new Set<string>();
  const out: Tab[] = [];
  for (let i = tabs.length - 1; i >= 0; i--) {
    const tab = tabs[i];
    if (seen.has(tab.id)) continue;
    seen.add(tab.id);
    out.unshift(tab);
  }
  return out;
}

/** Draft or LexGat system-template id encoded in a workspace tab path. */
export function draftPathEntityId(path: string): string | undefined {
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'drafts') return undefined;
  if (parts[1] === 'system' && parts[2]) {
    try {
      return decodeURIComponent(parts[2]);
    } catch {
      return parts[2];
    }
  }
  if (parts[1] && parts[1] !== 'system') return parts[1];
  return undefined;
}

/** Open a workspace case in the tab strip (call before navigate). */
export function openWorkspaceCaseTab(caseId: string, title = 'Loading...'): string {
  const path = `/cases/${caseId}`;
  clearSuppressAutoTab(path);
  useWorkspaceTabs.getState().addTab({
    id: path,
    path,
    title,
    type: 'case',
    entityId: caseId,
  });
  return path;
}

/** Open a workspace draft in the tab strip (call before navigate). */
export function openWorkspaceDraftTab(input: {
  docId: string;
  title: string;
  pendingAutoDraft?: Tab['pendingAutoDraft'];
}): string {
  const path = `/drafts/${input.docId}`;
  useWorkspaceTabs.getState().addTab({
    id: path,
    path,
    title: input.title,
    type: 'draft',
    entityId: input.docId,
    ...(input.pendingAutoDraft ? { pendingAutoDraft: input.pendingAutoDraft } : {}),
  });
  return path;
}

interface WorkspaceState {
  tabs: Tab[];
  activeTabId: string | null;
  lastSessionActiveAt: number | null;
  ownerUid: string | null;
  addTab: (tab: Omit<Tab, 'lastActiveAt'>) => void;
  removeTab: (id: string, callback?: (nextTabId: string | null) => void) => void;
  setActiveTab: (id: string | null) => void;
  updateTabTitle: (id: string, title: string) => void;
  updateTabPath: (oldPath: string, newPath: string, title: string) => void;
  clearTabs: () => void;
  updateLastActive: () => void;
  consumePendingAutoDraft: (tabId: string) => PendingAutoDraftPayload | null;
}

export const LEGACY_WORKSPACE_TABS_KEY = 'lexgat-workspace-tabs';

let activeTabsOwnerUid: string | null = null;

function workspaceTabsStorageKey(uid: string | null): string {
  return uid ? `lexgat-workspace-tabs-${uid}` : 'lexgat-workspace-tabs-guest';
}

export function resetWorkspaceTabsInMemory(): void {
  useWorkspaceTabs.setState({
    tabs: [],
    activeTabId: null,
    lastSessionActiveAt: null,
    ownerUid: null,
  });
}

export function clearLegacyWorkspaceTabsStorage(): void {
  localStorage.removeItem(LEGACY_WORKSPACE_TABS_KEY);
  sessionStorage.removeItem('lexgat_session_active');
}

/** Switch persisted tab storage to the signed-in user and reload their tab strip. */
export async function rehydrateWorkspaceTabsForUser(uid: string | null): Promise<void> {
  if (activeTabsOwnerUid !== uid) {
    resetWorkspaceTabsInMemory();
  }
  activeTabsOwnerUid = uid;
  clearLegacyWorkspaceTabsStorage();

  useWorkspaceTabs.persist.setOptions({
    name: workspaceTabsStorageKey(uid),
  });
  await useWorkspaceTabs.persist.rehydrate();

  const state = useWorkspaceTabs.getState();
  if (uid && state.ownerUid && state.ownerUid !== uid) {
    resetWorkspaceTabsInMemory();
    useWorkspaceTabs.setState({ ownerUid: uid });
    return;
  }
  if (uid && state.ownerUid !== uid) {
    useWorkspaceTabs.setState({ ownerUid: uid });
  }
}

export const useWorkspaceTabs = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,
      lastSessionActiveAt: null,
      ownerUid: null,
      addTab: (tab) => set((state) => {
        if (suppressAutoTabForPath === tab.id) {
          suppressAutoTabForPath = null;
        }
        const tabs = dedupeWorkspaceTabs(state.tabs);
        const exists = tabs.find((t) => t.id === tab.id);
        if (exists) {
          if (tab.pendingAutoDraft && !exists.pendingAutoDraft) {
            return {
              tabs: dedupeWorkspaceTabs(
                tabs.map((t) =>
                  t.id === tab.id ? { ...t, pendingAutoDraft: tab.pendingAutoDraft } : t,
                ),
              ),
              activeTabId: tab.id,
            };
          }
          return { tabs, activeTabId: tab.id };
        }
        return {
          tabs: dedupeWorkspaceTabs([...tabs, { ...tab, lastActiveAt: Date.now() }]),
          activeTabId: tab.id,
        };
      }),
      removeTab: (id, callback) => {
        const state = get();
        const newTabs = state.tabs.filter(t => t.id !== id);
        let nextActive = state.activeTabId;
        
        if (state.activeTabId === id) {
          const index = state.tabs.findIndex(t => t.id === id);
          if (newTabs.length > 0) {
            nextActive = newTabs[index]?.id ?? newTabs[Math.max(0, index - 1)]?.id ?? null;
          } else {
            nextActive = null;
          }
        }
        
        set({ tabs: dedupeWorkspaceTabs(newTabs), activeTabId: nextActive });
        if (callback) callback(nextActive);
      },
      setActiveTab: (id) => set({ activeTabId: id }),
      updateTabTitle: (id, title) => set((state) => ({
        tabs: state.tabs.map(t => t.id === id ? { ...t, title } : t)
      })),
      updateTabPath: (oldPath, newPath, title) =>
        set((state) => {
          const tabs = dedupeWorkspaceTabs(state.tabs);
          if (oldPath === newPath) {
            return {
              tabs: tabs.map((t) => (t.id === oldPath ? { ...t, title } : t)),
              activeTabId: state.activeTabId,
            };
          }

          const entityId = draftPathEntityId(newPath);
          const oldTab = tabs.find((t) => t.id === oldPath);
          const targetTab = tabs.find((t) => t.id === newPath);
          const rest = tabs.filter((t) => t.id !== oldPath && t.id !== newPath);

          const merged: Tab = {
            ...(targetTab ?? oldTab ?? {
              id: newPath,
              path: newPath,
              title,
              type: 'draft' as const,
              lastActiveAt: Date.now(),
            }),
            id: newPath,
            path: newPath,
            title,
            entityId: entityId ?? targetTab?.entityId ?? oldTab?.entityId,
            lastActiveAt: Math.max(
              targetTab?.lastActiveAt ?? 0,
              oldTab?.lastActiveAt ?? 0,
              Date.now(),
            ),
            ...(oldTab?.pendingAutoDraft && !targetTab?.pendingAutoDraft
              ? { pendingAutoDraft: oldTab.pendingAutoDraft }
              : {}),
          };

          return {
            tabs: dedupeWorkspaceTabs([...rest, merged]),
            activeTabId:
              state.activeTabId === oldPath || state.activeTabId === newPath
                ? newPath
                : state.activeTabId,
          };
        }),
      clearTabs: () => set({ tabs: [], activeTabId: null, ownerUid: activeTabsOwnerUid }),
      updateLastActive: () => set({ lastSessionActiveAt: Date.now() }),
      consumePendingAutoDraft: (tabId) => {
        const tab = get().tabs.find(t => t.id === tabId);
        if (!tab?.pendingAutoDraft) return null;

        const payload: PendingAutoDraftPayload = {
          instruction: tab.pendingAutoDraft.instruction,
          draftText: tab.pendingAutoDraft.draftText,
          templateMode: tab.pendingAutoDraft.templateMode,
          exemplarMode: tab.pendingAutoDraft.exemplarMode,
          templateTitle: tab.pendingAutoDraft.templateTitle,
          templateReference: tab.pendingAutoDraft.templateReference,
        };

        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, pendingAutoDraft: undefined } : t
          ),
        }));

        return payload;
      },
    }),
    {
      name: LEGACY_WORKSPACE_TABS_KEY,
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        lastSessionActiveAt: state.lastSessionActiveAt,
        ownerUid: state.ownerUid,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<WorkspaceState> | undefined;
        const mergedTabs = dedupeWorkspaceTabs(p?.tabs ?? []);
        const activeTabId = p?.activeTabId ?? current.activeTabId;
        const ownerUid = p?.ownerUid ?? activeTabsOwnerUid ?? current.ownerUid;
        if (ownerUid && activeTabsOwnerUid && ownerUid !== activeTabsOwnerUid) {
          return {
            ...current,
            tabs: [],
            activeTabId: null,
            ownerUid: activeTabsOwnerUid,
            lastSessionActiveAt: null,
          };
        }
        return {
          ...current,
          tabs: mergedTabs,
          activeTabId:
            activeTabId && mergedTabs.some((t) => t.id === activeTabId)
              ? activeTabId
              : mergedTabs[mergedTabs.length - 1]?.id ?? null,
          lastSessionActiveAt: p?.lastSessionActiveAt ?? current.lastSessionActiveAt,
          ownerUid,
        };
      },
    }
  )
);
